import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'

import useGoogleMaps from 'hooks/useGoogleMaps'
import useSearchOrigin from 'hooks/useSearchOrigin'
import { Crosshair, Loader2, MapPin, Pencil } from 'lucide-react'
import { Input } from 'components/ui/input'
import { cn } from 'lib/utils'
import { noAutofillProps } from 'lib/noAutofill'

/**
 * AddressAutocomplete uses Google Maps JS SDK (Places library) directly,
 * matching the original `<PlacesAutocomplete>` integration that worked
 * before the Photon experiment. The Google SDK handles user-location
 * detection internally (via Google's WiFi/cell databases), which is
 * materially more accurate than `navigator.geolocation` — that's why
 * the original implementation correctly ranked nearby results without
 * any explicit `proximity` prop.
 *
 * Requires `<script src="...maps/api/js?...&libraries=places"></script>`
 * to be loaded — handled at app boot in `public/index.html`.
 */

export interface AddressResultAddress {
	house_number?: string
	road?: string
	city?: string
	state?: string
	postcode?: string
	country?: string
}

export interface AddressResult {
	latitude: number
	longitude: number
	formattedAddress: string
	confidence: number
	osmId: string
	placeId?: string
	name?: string
	address: AddressResultAddress
	/**
	 * First Google Places photo URL for the selected place, if available.
	 * Short-lived signed URL — consumers that need persistence should
	 * upload to Cloudinary on receipt (see LocationAddNewCN/LocationEditPopupCN
	 * for the pattern). Honours `project_photo_requirement.md` so that
	 * picking an address auto-pulls a candidate photo.
	 */
	photoUrl?: string
}

interface Prediction {
	placeId: string
	primaryText: string
	secondaryText: string
	description: string
	distanceMeters?: number
}

export interface AddressAutocompleteProps {
	onSelect: (result: AddressResult) => void
	defaultValue?: string
	placeholder?: string
	className?: string
	inputClassName?: string
	disabled?: boolean
	/**
	 * An explicit origin to rank results around, which WINS over the browser's
	 * own fix.
	 *
	 * This was previously documented as unused, on the theory that Google's SDK
	 * works out proximity by itself. It does not do it well enough: without an
	 * origin it falls back to IP geolocation, which resolves to the ISP's point
	 * of presence and ranks a search around a town the user is merely near.
	 *
	 * Pass it whenever the screen knows somewhere better than the user's
	 * handset does. The map picker passes the centre of the visible map, which
	 * is almost always what the user means when they search while looking at it.
	 */
	proximity?: { lat: number; lng: number }
	minQueryLength?: number
	debounceMs?: number
	limit?: number
}

export interface AddressAutocompleteHandle {
	/**
	 * Empty the search box without remounting the component.
	 *
	 * Remounting was the old way to clear it, via a changing `key`. That
	 * removes a filled input from the DOM, which Chromium reads as a form
	 * submission and answers with its "Update address?" bubble. See
	 * `lib/noAutofill`.
	 */
	clear: () => void
}

type Status = 'idle' | 'loading' | 'ready' | 'empty' | 'error'

const ADDRESS_TYPE_MAP: Record<string, keyof AddressResultAddress> = {
	street_number: 'house_number',
	route: 'road',
	locality: 'city',
	postal_town: 'city',
	sublocality: 'city',
	administrative_area_level_1: 'state',
	postal_code: 'postcode',
	country: 'country',
}

function shapeAddressComponents(components: any[] = []): AddressResultAddress {
	const out: AddressResultAddress = {}
	for (const c of components) {
		for (const t of c.types || []) {
			const key = ADDRESS_TYPE_MAP[t]
			if (key && !out[key]) {
				out[key] = t === 'administrative_area_level_1' ? c.short_name : c.long_name
			}
		}
	}
	return out
}

/** Soft-bias radius handed to Google alongside the origin. */
const NEARBY_BIAS_METRES = 50000

function getGoogleNamespace(): any | null {
	if (typeof window === 'undefined') return null
	const g = (window as any).google
	if (!g?.maps?.places) return null
	return g
}

export const AddressAutocomplete = forwardRef<AddressAutocompleteHandle, AddressAutocompleteProps>(function AddressAutocomplete({
	onSelect,
	defaultValue = '',
	placeholder = 'Start typing an address...',
	className,
	inputClassName,
	disabled = false,
	proximity,
	minQueryLength = 3,
	debounceMs = 150,
	limit = 5,
}: AddressAutocompleteProps, ref) {
	const [query, setQuery] = useState(defaultValue)
	const [predictions, setPredictions] = useState<Prediction[]>([])
	const [status, setStatus] = useState<Status>('idle')
	const [open, setOpen] = useState(false)
	const [highlightedIndex, setHighlightedIndex] = useState(0)
	const [resolvingPlaceId, setResolvingPlaceId] = useState<string | null>(null)
	const inputRef = useRef<HTMLInputElement>(null)
	const containerRef = useRef<HTMLDivElement>(null)
	const placesAttachRef = useRef<HTMLDivElement>(null)
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const suppressNextSearchRef = useRef(false)
	const requestIdRef = useRef(0)

	// Pull the Google Maps SDK in when this component mounts. It used to be a
	// blocking <script> in index.html, so it was fetched on every page of the
	// app — including screens with no address input — and Google bills that per
	// page load (DT-012). `runSearch` already degrades gracefully while the
	// namespace is absent, so a slow load costs nothing but a moment of plain
	// text entry.
	useGoogleMaps()

	// The user's coarse position, used to bias predictions toward nearby
	// results. Google's IP-based default biasing alone is not reliable enough:
	// it is what puts a search for a local street in another metro entirely.
	//
	// Resolved once per tab and cached by the hook, so the second and later
	// dialogs in a session have the fix before the user types the first letter.
	// `runSearch` depends on it, so a fix that lands mid-session re-runs the
	// query in flight rather than leaving an unbiased result list on screen.
	const {
		origin: searchOrigin,
		label: originLabel,
		source: originSource,
		isPrecise,
		status: locationStatus,
		setOrigin,
		useDeviceLocation,
	} = useSearchOrigin()

	/**
	 * Where results get ranked from. An origin the screen supplied always wins:
	 * it knows what the user is looking at, and the device only knows what its
	 * location settings were told to say.
	 */
	/*
		Keyed on the numbers, never on the objects. `proximity` arrives from a
		caller we do not control and may well be an object literal, and this
		value ends up in `runSearch`'s dependency list, where a new identity on
		every render means a new Places request on every render.
	*/
	const originLat = proximity ? proximity.lat : searchOrigin?.lat
	const originLng = proximity ? proximity.lng : searchOrigin?.lng
	const origin = useMemo(
		() =>
			typeof originLat === 'number' && typeof originLng === 'number'
				? { lat: originLat, lng: originLng }
				: null,
		[originLat, originLng],
	)

	/**
	 * In 'origin' mode the box picks the place to search NEAR rather than the
	 * place to fill the form with.
	 *
	 * Reusing this input rather than building a second picker: it already
	 * debounces, ranks and resolves coordinates, and a second autocomplete
	 * nested inside this one's dropdown would be its own small nightmare.
	 */
	const [pickingOrigin, setPickingOrigin] = useState(false)
	const queryBeforePickRef = useRef('')

	const startPickingOrigin = useCallback(() => {
		queryBeforePickRef.current = query
		setPickingOrigin(true)
		suppressNextSearchRef.current = true
		setQuery('')
		setPredictions([])
		setStatus('idle')
		inputRef.current?.focus()
	}, [query])

	const stopPickingOrigin = useCallback(() => {
		setPickingOrigin(false)
		// Put the user's search back so choosing an origin does not cost them
		// the thing they were typing.
		setQuery(queryBeforePickRef.current)
	}, [])

	/**
	 * What the footer says about the origin. Never "you" unless we have a
	 * reason to believe it: an unnamed, kilometres-wide fix gets its radius
	 * printed instead, because that number is the thing that explains why the
	 * results look wrong.
	 */
	const originDescription = useMemo(() => {
		if (originSource === 'chosen') return `Near ${originLabel}`
		if (!searchOrigin) return 'Results are not sorted by distance'
		if (originLabel) return `Near ${originLabel}${isPrecise ? '' : ' (approximate)'}`
		return isPrecise ? 'Nearest to you first' : 'Approximate location'
	}, [originSource, originLabel, searchOrigin, isPrecise])

	useImperativeHandle(
		ref,
		() => ({
			clear: () => {
				suppressNextSearchRef.current = true
				setQuery('')
				setPredictions([])
				setStatus('idle')
				setOpen(false)
			},
		}),
		[],
	)

	const closeDropdown = useCallback(() => {
		setOpen(false)
		setHighlightedIndex(0)
	}, [])

	const runSearch = useCallback(
		(q: string) => {
			const google = getGoogleNamespace()
			if (!google) {
				setStatus('error')
				return
			}
			setStatus('loading')
			setOpen(true)
			const myRequestId = ++requestIdRef.current
			const service = new google.maps.places.AutocompleteService()

			/*
				ONE request per search, deliberately.

				`location` + `radius` is a SOFT bias: Google blends proximity with
				prominence and returns a capped prediction list, so a chain query
				can fill that cap with prominent branches and omit the one three
				miles away. A second pass with `strictBounds` over a box around
				the origin was tried here to force local candidates into the list.
				It did not fix the report, and it doubled the Places Autocomplete
				spend on every one of these boxes, so it was removed rather than
				left running as a cost with no benefit.

				MYD-480 carries that investigation. The likely answer is that
				`AutocompleteService` is the wrong API for "the nearest branch of
				X" and that Places Text Search with `rankBy: DISTANCE` is the
				right one, rather than any further tuning of the bias.
			*/
			const request: any = { input: q }
			if (origin) {
				const ll = new google.maps.LatLng(origin.lat, origin.lng)
				request.location = ll
				request.radius = NEARBY_BIAS_METRES
				request.origin = ll // returns distance_meters per prediction
			}

			service.getPlacePredictions(request, (results: any[] | null, statusCode: any) => {
				if (myRequestId !== requestIdRef.current) return
				const OK = google.maps.places.PlacesServiceStatus.OK
				const ZERO = google.maps.places.PlacesServiceStatus.ZERO_RESULTS
				if (statusCode === ZERO || !results || results.length === 0) {
					setPredictions([])
					setStatus('empty')
					return
				}
				if (statusCode !== OK) {
					setPredictions([])
					setStatus('error')
					return
				}

				const shaped: Prediction[] = results.map((r) => ({
					placeId: r.place_id,
					primaryText: r.structured_formatting?.main_text || r.description,
					secondaryText: r.structured_formatting?.secondary_text || '',
					description: r.description,
					distanceMeters:
						typeof r.distance_meters === 'number' ? r.distance_meters : undefined,
				}))

				// Hard sort by actual distance when we have it. Predictions
				// without distance (Google only returns it for some result
				// types) bubble to the end so user-relevant matches still
				// surface even when Google didn't compute a distance.
				//
				// Not while picking an origin: the list is towns to search near,
				// and ranking those by distance from the origin being replaced
				// would bury the one the user is reaching for.
				if (origin && !pickingOrigin) {
					shaped.sort((a, b) => {
						const da = a.distanceMeters ?? Number.POSITIVE_INFINITY
						const db = b.distanceMeters ?? Number.POSITIVE_INFINITY
						return da - db
					})
				}

				setPredictions(shaped.slice(0, limit))
				setStatus('ready')
				setHighlightedIndex(0)
			})
		},
		[limit, origin, pickingOrigin],
	)

	useEffect(() => {
		if (suppressNextSearchRef.current) {
			suppressNextSearchRef.current = false
			return
		}
		if (debounceRef.current) clearTimeout(debounceRef.current)
		const trimmed = query.trim()
		if (trimmed.length < minQueryLength) {
			setPredictions([])
			setStatus('idle')
			setOpen(false)
			return
		}
		debounceRef.current = setTimeout(() => runSearch(trimmed), debounceMs)
		return () => {
			if (debounceRef.current) clearTimeout(debounceRef.current)
		}
	}, [query, minQueryLength, debounceMs, runSearch])

	useEffect(() => {
		if (!open) return
		const handleClickOutside = (e: MouseEvent) => {
			if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
				closeDropdown()
				// Clicking away abandons the origin pick too, rather than leaving
				// the box silently still in a mode the user has forgotten about.
				if (pickingOrigin) stopPickingOrigin()
			}
		}
		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [open, closeDropdown, pickingOrigin, stopPickingOrigin])

	const handleSelect = useCallback(
		(prediction: Prediction) => {
			const google = getGoogleNamespace()
			if (!google || !placesAttachRef.current) {
				setStatus('error')
				return
			}
			setResolvingPlaceId(prediction.placeId)
			const service = new google.maps.places.PlacesService(placesAttachRef.current)
			service.getDetails(
				{
					placeId: prediction.placeId,
					fields: [
						'place_id',
						'name',
						'formatted_address',
						'geometry',
						'address_components',
						'photos',
					],
				},
				(place: any, statusCode: any) => {
					setResolvingPlaceId(null)
					const OK = google.maps.places.PlacesServiceStatus.OK
					if (statusCode !== OK || !place) {
						setStatus('error')
						return
					}
					const lat = place.geometry?.location?.lat?.()
					const lng = place.geometry?.location?.lng?.()
					// Request the largest reasonable size — consumers can downsize on
					// upload via Cloudinary transforms. Matches ImagePickerPopup's
					// 800px choice for visual parity.
					const photoUrl: string | undefined =
						place.photos?.[0]?.getUrl?.({ maxWidth: 800 }) || undefined
					const result: AddressResult = {
						placeId: place.place_id,
						osmId: `place:${place.place_id}`,
						name: place.name || prediction.primaryText,
						formattedAddress: place.formatted_address || prediction.description,
						latitude: typeof lat === 'number' ? lat : 0,
						longitude: typeof lng === 'number' ? lng : 0,
						confidence: 1,
						address: shapeAddressComponents(place.address_components),
						photoUrl,
					}
					// Picking the place to search NEAR, not the place to keep.
					if (pickingOrigin) {
						setOrigin({
							lat: result.latitude,
							lng: result.longitude,
							// The short name reads better in "Near Hollister, CA"
							// than a full street address would.
							label: prediction.primaryText || result.name || result.formattedAddress,
						})
						suppressNextSearchRef.current = true
						stopPickingOrigin()
						closeDropdown()
						return
					}

					suppressNextSearchRef.current = true
					setQuery(result.formattedAddress)
					closeDropdown()
					onSelect(result)
				},
			)
		},
		[onSelect, closeDropdown, pickingOrigin, setOrigin, stopPickingOrigin],
	)

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (!open || predictions.length === 0) {
			if (e.key === 'Escape') closeDropdown()
			return
		}
		if (e.key === 'ArrowDown') {
			e.preventDefault()
			setHighlightedIndex((i) => Math.min(i + 1, predictions.length - 1))
		} else if (e.key === 'ArrowUp') {
			e.preventDefault()
			setHighlightedIndex((i) => Math.max(i - 1, 0))
		} else if (e.key === 'Enter') {
			e.preventDefault()
			const selected = predictions[highlightedIndex]
			if (selected) handleSelect(selected)
		} else if (e.key === 'Escape') {
			e.preventDefault()
			closeDropdown()
		}
	}

	return (
		<div ref={containerRef} className={cn('relative w-full', className)}>
			{/* Required attach point for PlacesService — must be in the DOM */}
			<div ref={placesAttachRef} className="hidden" />
			<Input
				ref={inputRef}
				type="text"
				value={query}
				onChange={(e) => setQuery(e.target.value)}
				onKeyDown={handleKeyDown}
				onFocus={() => {
					if (status !== 'idle' && query.trim().length >= minQueryLength) setOpen(true)
				}}
				placeholder={pickingOrigin ? 'Type a town, city or postal code...' : placeholder}
				disabled={disabled}
				className={cn('w-full', inputClassName)}
				{...noAutofillProps}
				role="combobox"
				aria-expanded={open}
				aria-autocomplete="list"
			/>
			{/* Held open while picking an origin, so the prompt and the way out
			    of it are on screen before the user has typed anything. */}
			{(open || pickingOrigin) && (
				<div className="absolute left-0 right-0 top-full z-50 mt-1 rounded-md border bg-popover text-popover-foreground shadow-md">
					{status === 'loading' && (
						<div className="flex items-center gap-2 px-3 py-3 text-sm text-muted-foreground">
							<Loader2 className="h-4 w-4 animate-spin" />
							Searching...
						</div>
					)}
					{status === 'empty' && (
						<div className="px-3 py-3 text-sm text-muted-foreground">No matches</div>
					)}
					{status === 'error' && (
						<div className="px-3 py-3 text-sm text-destructive">Geocoding unavailable</div>
					)}
					{status === 'ready' && (
						<ul
							className="max-h-[280px] overflow-y-auto py-1 list-none"
							role="listbox"
							aria-label="Address suggestions"
						>
							{predictions.map((prediction, idx) => {
								const isActive = idx === highlightedIndex
								const isResolving = resolvingPlaceId === prediction.placeId
								return (
									<li
										key={prediction.placeId}
										role="option"
										aria-selected={isActive || undefined}
										onClick={() => !isResolving && handleSelect(prediction)}
										onMouseEnter={() => setHighlightedIndex(idx)}
										className={cn(
											'flex cursor-pointer items-start gap-2 px-3 py-2 text-sm transition-colors',
											isActive ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/60',
											isResolving && 'opacity-60',
										)}
									>
										{isResolving ? (
											<Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
										) : (
											<MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
										)}
										<span className="flex flex-col min-w-0">
											<span className="truncate font-medium">{prediction.primaryText}</span>
											{prediction.secondaryText && (
												<span className="truncate text-xs text-muted-foreground">
													{prediction.secondaryText}
												</span>
											)}
										</span>
									</li>
								)
							})}
						</ul>
					)}

					{/* Why results are ordered the way they are, and a way to fix it
					    when they are not. Without this the user has no way to tell
					    a denied permission from a bad match. */}
					{/*
						Name the place we are ranking around, and let the user
						correct it.

						"Nearest to you first" was the whole problem: it sounds
						like the feature is working even when the origin is a town
						the user has never been to. A device can report a made-up
						position confidently (Windows' "default location" does
						exactly that), and nothing in the Geolocation API says so.
						Printing the town turns an invisible fault into a one-click
						correction.
					*/}
					{(status === 'ready' || status === 'empty' || pickingOrigin) && (
						<div className="flex items-center justify-between gap-2 border-t px-3 py-1.5 text-[11px] text-muted-foreground">
							{proximity ? (
								<span className="flex items-center gap-1">
									<Crosshair className="h-3 w-3" />
									Nearest to the map first
								</span>
							) : pickingOrigin ? (
								<>
									<span className="flex items-center gap-1 font-medium text-primary">
										<Crosshair className="h-3 w-3" />
										Pick the town to search near
									</span>
									<button
										type="button"
										onMouseDown={(e) => e.preventDefault()}
										onClick={stopPickingOrigin}
										className="whitespace-nowrap border-0 bg-transparent p-0 font-medium text-primary hover:underline"
									>
										Cancel
									</button>
								</>
							) : locationStatus === 'locating' ? (
								<span className="flex items-center gap-1">
									<Loader2 className="h-3 w-3 animate-spin" />
									Finding your location...
								</span>
							) : (
								<>
									<span className="flex min-w-0 items-center gap-1">
										<Crosshair className="h-3 w-3 shrink-0" />
										<span className="truncate">{originDescription}</span>
									</span>
									<span className="flex shrink-0 items-center gap-2">
										{originSource === 'chosen' && (
											<button
												type="button"
												onMouseDown={(e) => e.preventDefault()}
												onClick={useDeviceLocation}
												className="whitespace-nowrap border-0 bg-transparent p-0 font-medium text-primary hover:underline"
											>
												Use my location
											</button>
										)}
										<button
											type="button"
											onMouseDown={(e) => e.preventDefault()}
											onClick={startPickingOrigin}
											className="flex items-center gap-1 whitespace-nowrap border-0 bg-transparent p-0 font-medium text-primary hover:underline"
										>
											<Pencil className="h-3 w-3" />
											Change
										</button>
									</span>
								</>
							)}
						</div>
					)}
				</div>
			)}
		</div>
	)
})
